<template>
  <SoWidget :disabled="disabled">
    <div class="d-flex align-center mb-3">
      <SoAvatar ref="ihd-icon" type="ihd" :disabled="disabled" />
      <h5 class="ml-2 mr-auto">In-Home Display</h5>
      <SoStatus data-testid="ihd-status" :status="statusType" />
    </div>
    <div v-if="!disabled" data-testid="ihd-expand-details">
      <p :class="{ 'mb-3': !hasGuid }" data-testid="ihd-guid">
        GUID: {{ ihd.deviceId || "Not found" }}
        <SoCopyToClipboard
          v-if="hasGuid"
          ref="copy-guid"
          inline
          :plain-text="ihd.deviceId"
        >
        </SoCopyToClipboard>
      </p>
      <p class="mb-3" data-testid="ihd-device-type">
        Device type: {{ deviceTypeText }}
      </p>
      <p class="mb-3" data-testid="ihd-installer">
        Provided by: {{ installedByText }}
      </p>
      <p class="mb-3" data-testid="ihd-date-commissioned">
        Date commissioned: {{ formatDateContent(ihd.dateCommissioned, "N/A") }}
      </p>
      <p class="mb-3" data-testid="ihd-dcc-responsive">
        DCC Responsive: {{ ihd.responsiveStatus || "N/A" }}
      </p>
      <p class="mb-3" data-testid="ihd-data-updated">
        Data Last Updated: {{ dataLastUpdated }}
      </p>
      <div>
        <SoButton
          v-if="!noIhd"
          icon="chevron-right"
          title="See IHD Details"
          type="text"
          @click="showIhdModal = true"
        />
        <SoButton
          :href="`https://soenergy.typeform.com/to/${typeformIds.IHD_TROUBLESHOOTING}`"
          title="IHD troubleshooting"
          icon="external-link"
          type="text"
          target="_blank"
        />
      </div>
    </div>
    <IhdModal
      ref="ihd-modal"
      v-model="showIhdModal"
      :ihd="ihd"
      :title="modalTitle"
      @click="showTypeform"
    />
  </SoWidget>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoWidget from "@/components/SoWidget.vue"
  import { IhdDetails } from "@/models/ihdDetails/ihdDetails"
  import SoAvatar from "@/components/SoAvatar.vue"
  import SoCopyToClipboard from "@/components/SoCopyToClipboard.vue"
  import dayjs, { Dayjs } from "dayjs"
  import { IhdStatus, ihdStatuses } from "@/models/ihdDetails/ihdDetails.types"
  import SoStatus from "@/components/SoStatus.vue"
  import { IStatus } from "@/types/Status"
  import IhdModal from "../modals/IhdModal.vue"
  import TypeformService from "@/services/TypeformService"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { typeformIds } from "@/constants/typeformIds"

  export default defineComponent({
    components: {
      SoWidget,
      SoAvatar,
      SoCopyToClipboard,
      SoStatus,
      IhdModal,
      SoButton,
    },
    props: {
      ihd: {
        type: Object as PropType<IhdDetails>,
        default: () => ({}),
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        showIhdModal: false,
        typeformIds,
      }
    },
    computed: {
      statusType(): IStatus {
        return this.disabled
          ? { type: "default", text: "Not supplied" }
          : (this.ihd.ihdStatusType ?? ihdStatuses[IhdStatus.NO_IHD])
      },
      hasGuid() {
        return this.ihd.deviceId
      },
      installedByText() {
        return this.ihd.installedBy
          ? `${this.ihd.installedBy} on ${this.formatDateContent(
              this.ihd.dateInstalled,
              "unknown date",
            )}`
          : "N/A"
      },
      deviceTypeText(): string {
        if (this.noIhd) return "N/A"
        const { manufacturerName, modelName, deviceType } = this.ihd

        if (manufacturerName && modelName) {
          return `${manufacturerName} ${modelName} (${deviceType})`
        }

        const name = manufacturerName || modelName || "Unknown"
        return `${name} (${deviceType})`
      },
      noIhd(): boolean {
        return Object.keys(this.ihd).length === 0
      },
      dataLastUpdated() {
        return this.ihd.dateLastUpdated
          ? dayjs(this.ihd.dateLastUpdated).format("DD MMM YYYY HH:mm")
          : "N/A"
      },
      modalTitle() {
        return `In-Home Display (GUID ${this.ihd.deviceId})`
      },
    },
    methods: {
      formatDateContent(value: Dayjs | undefined, fallback: string) {
        return value ? dayjs(value).format("DD MMM YYYY") : fallback
      },
      showTypeform() {
        this.showIhdModal = false
        TypeformService.openPopup(typeformIds.IHD_DETAILS, {
          height: 500,
          width: 700,
        })
      },
    },
  })
</script>
